import { Injectable, signal, WritableSignal } from '@angular/core';
import {
  Configuration,
  ModulesTournamentsApplicationRoundsCloseRoundCloseRoundResponse,
  ModulesTournamentsEndpointsContractsTournamentPlayersUpdateTournamentPlayerRequestModel,
  ModulesTournamentsEndpointsContractsTournamentsUpdateTournamentRequestModel,
  TournamentsService,
} from '../../api-client';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import {
  Tournament,
  Round,
  RoundTournamentCourse,
  TournamentCourse,
  TournamentScoringType,
  TournamentClassification,
} from '../../models';
import { TournamentPlayer } from '../../models/tournamentPlayer';
import { TournamentPlayerType } from '../../models/tournamentPlayerType';
import { ConfigService } from '../config/config.service';
import { LoggingService } from '../shared/logging/logging.service';
import { NavigationRoute } from '../../models/config';

@Injectable({
  providedIn: 'root',
})
export class TournamentService extends TournamentsService {
  constructor(
    httpClient: HttpClient,
    config: Configuration,
    configService: ConfigService,
    private loggingService: LoggingService
  ) {
    super(httpClient, config.basePath!, config);

    // get tournament navItem from config navigation
    this.tournamentsNavItem = configService.config?.navigation?.find(
      (item) => item.name === 'Tournaments'
    );

    this.loggingService.debug('TournamentService', 'TournamentService initialized');

    // Set up signal logging
    this.selectedTournamentSignal.update((val) => {
      this.loggingService.debug('TournamentService', 'Initial selectedTournamentSignal value:', val?.name || 'null');
      return val;
    });

    this.selectedRoundSignal.update((val) => {
      this.loggingService.debug('TournamentService', 'Initial selectedRoundSignal value:', val ? `Round ${val.number}` : 'null');
      return val;
    });
  }
  tournamentsNavItem: NavigationRoute | undefined = undefined;

  // Signals for selections
  selectedTournamentSignal: WritableSignal<Tournament | null> = signal(null);
  selectedRoundSignal: WritableSignal<Round | null> = signal(null);

  private _selectedTournament: Tournament | null = null;
  set selectedTournament(tournament: Tournament | null) {
    this.loggingService.debug('TournamentService', 'Setting selectedTournament signal:', tournament?.name || 'null');
    this._selectedTournament = tournament;
    this.selectedTournamentSignal.set(tournament);
    if (tournament?.tournamentId) {
      this.loggingService.debug('TournamentService', 'Getting rounds for tournament:', tournament.name);
      // get rounds for the selected tournament
      this.getRounds(tournament.tournamentId).subscribe((rounds) => {
        const activeRound = rounds.find((r) => r.active) || null;
        this.loggingService.debug('TournamentService', 'Setting selectedRound from tournament change:', activeRound ? `Round ${activeRound.number}` : 'null');
        this.selectedRoundSignal.set(activeRound);
      });
    }
  }
  get selectedTournament(): Tournament | null {
    return this._selectedTournament;
  }

  private _selectedRound: Round | null = null;
  set selectedRound(round: Round | null) {
    this.loggingService.debug('TournamentService', 'Setting selectedRound signal:', round ? `Round ${round.number}` : 'null');
    this._selectedRound = round;
    this.selectedRoundSignal.set(round);
  }
  get selectedRound(): Round | null {
    return this._selectedRound;
  }

  /**
   * Get all tournaments directly from API
   */
  getAll(): Observable<Tournament[]> {
    return super.getTournamentsEndpoint().pipe(
      map((tournaments: Tournament[]) =>
        tournaments.map((tournament) => ({
          ...tournament,
        }))
      )
    );
  }

  /**
   * Get the active tournament
   */
  getActive(): Observable<Tournament | undefined> {
    return this.getAll().pipe(
      map((tournaments) => tournaments.find((tournament) => tournament.active))
    );
  }

  /**
   * Get tournament by ID (no caching)
   */
  getById(tournamentId: string): Observable<Tournament> {
    return super.getTournamentByIdEndpoint(tournamentId).pipe(
      map((tournament) => ({
        ...tournament,
      }))
    );
  }

  /**
   * Get rounds for a tournament directly from API
   */
  getRounds(tournamentId: string): Observable<Round[]> {
    return super
      .getRoundsByTournamentIdEndpoint(tournamentId)
      .pipe(
        map((rounds: Round[]) => rounds.map((round) => ({ ...round })))
      );
  }

  closeRound(
    tournamentId: string,
    roundId: string,
    force: boolean = false
  ): Observable<ModulesTournamentsApplicationRoundsCloseRoundCloseRoundResponse> {
    const url = `${this.basePath}/tournaments/${tournamentId}/rounds/${roundId}/close`;
    const params = force ? new HttpParams().set('force', 'true') : undefined;

    // Include authentication headers consistent with generated client
    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.post<ModulesTournamentsApplicationRoundsCloseRoundCloseRoundResponse>(
      url,
      null,
      {
        ...(params ? { params } : {}),
        headers,
        withCredentials: this.configuration.withCredentials
      }
    );
  }

  /**
   * Activate a round (make it available for scoring)
   * This fetches the round, updates it with active=true, and saves it via UpdateRound endpoint
   */
  activateRound(
    tournamentId: string,
    roundId: string
  ): Observable<void> {
    // Fetch the round first to get all its properties
    return this.getRoundByIdEndpoint(tournamentId, roundId).pipe(
      switchMap(round => {
        // Update the round with active=true using the UpdateRound endpoint
        const updateModel = {
          number: round.number ?? 0,
          startDate: round.startDate,
          endDate: round.endDate,
          active: true,
          official: round.official ?? false,
          cutSize: round.cutSize,
          cutType: round.cutType,
          resetScores: round.resetScores,
          templateId: round.templateId
        };
        return this.tournamentsTournamentIdRoundsRoundIdPut(tournamentId, roundId, updateModel);
      }),
      map(() => undefined) // Convert to void
    );
  }

  getUnofficialScorecards(
    tournamentId: string,
    roundId: string
  ): Observable<{ unofficialScorecardCount: number; unofficialScorecards: any[] }> {
    const url = `${this.basePath}/tournaments/${tournamentId}/rounds/${roundId}/unofficial-scorecards`;

    // Include authentication headers consistent with generated client
    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.get<{ unofficialScorecardCount: number; unofficialScorecards: any[] }>(
      url,
      {
        headers,
        withCredentials: this.configuration.withCredentials
      }
    );
  }

  /**
   * Create a tournament course
   */
  createTournamentCourse(
    tournamentId: string,
    courseId: string
  ): Observable<any> {
    // If readonly mode is enabled, return 0 without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(0);
    }

    return super.tournamentsTournamentIdCoursesCourseIdPost(
      tournamentId,
      courseId
    );
  }

  /**
   * Get a tournament course (no caching)
   */
  getTournamentCourse(
    tournamentId: string,
    courseId: string
  ): Observable<TournamentCourse> {
    return super.getTournamentCourseEndpoint(tournamentId, courseId);
  }

  /**
   * Get all tournament courses for a tournament (no caching)
   */
  getTournamentCoursesByTournamentId(
    tournamentId: string
  ): Observable<TournamentCourse[]> {
    return super.getTournamentCoursesByTournamentIdEndpoint(tournamentId);
  }

  /**
   * Delete a tournament course (remove course from tournament)
   */
  deleteTournamentCourse(
    tournamentId: string,
    courseId: string
  ): Observable<any> {
    // If readonly mode is enabled, return empty observable without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(undefined);
    }

    return super.tournamentsTournamentIdCoursesCourseIdDelete(
      tournamentId,
      courseId
    );
  }

  /**
   * Create a round tournament course
   */
  createRoundTournamentCourse(
    tournamentId: string,
    courseId: string,
    roundId: string
  ): Observable<any> {
    // If readonly mode is enabled, return empty observable without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(undefined);
    }

    return super.tournamentsTournamentIdCoursesCourseIdRoundsRoundIdPost(
      tournamentId,
      courseId,
      roundId
    );
  }

  /**
   * Get a round tournament course (no caching)
   */
  getRoundTournamentCourse(
    tournamentId: string,
    courseId: string,
    roundId: string
  ): Observable<RoundTournamentCourse> {
    return super.getRoundTournamentCourseEndpoint(
      tournamentId,
      courseId,
      roundId
    );
  }



  markTournamentActive(tournamentId: string): Observable<string> {
    // If readonly mode is enabled, return empty observable without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(undefined as any);
    }

    return super.tournamentsTournamentIdMarkActivePost(tournamentId);
  }

  updateTournament(
    tournamentId: string,
    tournament: Tournament
  ): Observable<Tournament> {
    // If readonly mode is enabled, just return the tournament by ID without updating
    if (this.tournamentsNavItem?.readonly) {
      return this.getById(tournamentId);
    }

    const templateId = 1; // HACK: Replace with actual template ID

    const updateRequestModel: ModulesTournamentsEndpointsContractsTournamentsUpdateTournamentRequestModel =
    {
      lpgaId: tournament.importSourceId,
      tourId: tournament.tourId,
      templateId,
      year: tournament.year,
      name: tournament.name,
      number: tournament.number,
      city: tournament.city,
      stateId: tournament.stateId,
      countryId: tournament.countryId,
      startDate: tournament.startDate,
      endDate: tournament.endDate,
      purse: tournament.purse,
      sponsor: tournament.sponsor,
      sponsorSeries: tournament.sponsorSeries,
      trimester: tournament.trimester,
      seasonHalf: tournament.seasonHalf,
      playoffCategory: tournament.playoffCategory,
      scoringTypeId: tournament.scoringTypeId,
      active: tournament.active,
      official: tournament.official,
      settings: tournament.settings,
      classificationIds: tournament.classificationIds,
      externalIds: tournament.externalIds,
    };

    return super
      .tournamentsTournamentIdPut(tournamentId, updateRequestModel)
      .pipe(
        switchMap(() => this.getById(tournamentId))
      );
  }

  getTournamentPlayers(tournamentId: string): Observable<TournamentPlayer[]> {
    return super.getTournamentPlayersEndpoint(tournamentId);
  }

  // private mapPlayerTypesToPlayers(
  //   players: TournamentPlayer[],
  //   playerTypes: TournamentPlayerType[]
  // ): TournamentPlayer[] {
  //   players.forEach((player) => {
  //     player.tournamentPlayerTypes = playerTypes.filter(
  //       (type) => type.playerId === player.playerId
  //     );
  //     player.tournamentPlayerTypesDisplay = Array.isArray(
  //       player.tournamentPlayerTypes
  //     )
  //       ? player.tournamentPlayerTypes
  //           ?.map((type) => type?.name || '')
  //           .join(', ')
  //       : '';
  //   });
  //   return players;
  // }

  getTournamentPlayerTypes(): Observable<TournamentPlayerType[]> {
    return super.getTournamentPlayerTypesEndpoint();
  }

  updateTournamentPlayer(
    tournamentId: string,
    playerId: string,
    tournamentPlayer: TournamentPlayer
  ): Observable<TournamentPlayer> {
    // If readonly mode is enabled, just return the tournament player by ID without updating
    if (this.tournamentsNavItem?.readonly) {
      return super.getTournamentPlayerEndpoint(tournamentId, playerId);
    }

    const updateRequestModel = {
      categoryId: tournamentPlayer.categoryId,
      categoryRank: tournamentPlayer.categoryRank ?? 0,
      fieldRank: tournamentPlayer.fieldRank ?? 0,
      abrank: tournamentPlayer.abrank ?? 0,
      money: tournamentPlayer.money ?? 0,
      r1Wave: tournamentPlayer.r1Wave || null,
    } as ModulesTournamentsEndpointsContractsTournamentPlayersUpdateTournamentPlayerRequestModel;

    return super
      .tournamentsTournamentIdPlayersPlayerIdPut(
        tournamentId,
        playerId,
        updateRequestModel
      )
      .pipe(
        switchMap(() =>
          super.getTournamentPlayerEndpoint(tournamentId, playerId)
        )
      );
  }

  deleteTournamentPlayer(
    tournamentId: string,
    playerId: string
  ): Observable<void> {
    // If readonly mode is enabled, return empty observable without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(undefined as any);
    }

    return super
      .tournamentsTournamentIdPlayersPlayerIdDelete(tournamentId, playerId);
  }

  // add a getTournamentPlayer method to get a single player by id
  getTournamentPlayer(
    tournamentId: string,
    playerId: string
  ): Observable<TournamentPlayer> {
    return super.getTournamentPlayerEndpoint(tournamentId, playerId);
  }

  createTournamentPlayer(
    tournamentId: string,
    playerId: string,
    request: any
  ): Observable<number> {
    // If readonly mode is enabled, return 0 without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(0);
    }

    // Manual HTTP call since swagger codegen didn't generate this method properly
    const url = `${this.basePath}/tournaments/${tournamentId}/players/${playerId}`;

    // Include authentication headers consistent with generated client
    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.post<number>(url, request, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  assignPlayerBrackets(
    tournamentId: string,
    maxFieldSize: number,
    aBracketSize: number,
    bBracketSize: number,
    cBracketSize: number
  ): Observable<void> {
    // If readonly mode is enabled, return empty observable without making HTTP request
    if (this.tournamentsNavItem?.readonly) {
      return of(undefined as any);
    }

    const requestModel = {
      maxFieldSize,
      aBracketSize,
      bBracketSize,
      cBracketSize
    };

    const url = `${this.basePath}/tournaments/${tournamentId}/players/assign-brackets`;

    // Include authentication headers consistent with generated client
    let headers = this.defaultHeaders;
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    return this.httpClient.post<void>(url, requestModel, {
      headers,
      withCredentials: this.configuration.withCredentials
    });
  }

  getTournamentScoringTypes(): Observable<TournamentScoringType[]> {
    return super.getTournamentScoringTypesEndpoint();
  }

  getTournamentClassifications(): Observable<TournamentClassification[]> {
    return super.getTournamentClassificationsEndpoint();
  }

  getTournamentClassificationsById(
    tournamentId: string
  ): Observable<TournamentClassification[]> {
    return super.getTournamentClassificationsByTournamentIdEndpoint(
      tournamentId
    );
  }

  // Player Management Operations
  moveAlternateToField(tournamentId: string, playerId: string): Observable<any> {
    return super.tournamentsTournamentIdPlayersPlayerIdMoveToFieldPost(tournamentId, playerId);
  }

  movePlayerToAlternates(tournamentId: string, playerId: string, confirmDelete: boolean): Observable<any> {
    return super.tournamentsTournamentIdPlayersPlayerIdMoveToAlternatesPost(
      tournamentId,
      playerId,
      { confirmDelete }
    );
  }

  swapPlayerWithAlternate(
    tournamentId: string,
    fieldPlayerId: string,
    alternatePlayerId: string,
    confirmTransfer: boolean
  ): Observable<any> {
    return super.tournamentsTournamentIdPlayersSwapPost(tournamentId, {
      fieldPlayerId,
      alternatePlayerId,
      confirmTransfer,
    });
  }
}
